/*!
 * @copyright
 * Copyright(c) 2019 SICK AG
 */
/**
 * Polyfill for mouse `wheel` events.
 *
 * Based on the official `wheel` polyfill from the MDN documentation.
 *
 * This is used to unify the wheel event data, especially the delta* values
 * (firefox produces different values then the other browser engines.. )
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/Events/wheel
 *
 * @example
 *  new WheelListener().onWheel(elem, (e) => { console.log(e.deltaY); e.preventDefault(); });
 */
export class WheelListener {
    constructor() {
        this._prefix = '';
        // detect event model
        if (window.addEventListener) {
            this._addEventListener = 'addEventListener';
        }
        else {
            this._addEventListener = 'attachEvent';
            this._prefix = 'on';
        }
        // detect available wheel event
        this._support = 'onwheel' in document.createElement('div') ? 'wheel' : // Modern browsers support "wheel"
            // tslint:disable-next-line:no-any
            document.onmousewheel !== undefined ? 'mousewheel' : // Webkit and IE support at least "mousewheel"
                'DOMMouseScroll'; // let's assume that remaining browsers are older Firefox
    }
    onWheel(elem, callback, useCapture = false) {
        this._addWheelListener(elem, this._support, callback, useCapture);
        // handle MozMousePixelScroll in older Firefox
        if (this._support === 'DOMMouseScroll') {
            this._addWheelListener(elem, 'MozMousePixelScroll', callback, useCapture);
        }
    }
    _addWheelListener(elem, eventName, callbackFn, useCapture) {
        // tslint:disable-next-line:no-any
        const callback = (wheelEv) => {
            callbackFn(Object.assign({}, wheelEv, { deltaMode: 0, deltaX: (wheelEv.deltaX || 0) * Math.round(wheelEv.deltaMode === 1 ? WheelListener.deltaOneWheelFactor : 1), deltaY: (wheelEv.deltaY || 0) * Math.round(wheelEv.deltaMode === 1 ? WheelListener.deltaOneWheelFactor : 1), deltaZ: (wheelEv.deltaZ || 0) * Math.round(wheelEv.deltaMode === 1 ? WheelListener.deltaOneWheelFactor : 1) }));
        };
        // tslint:disable-next-line:no-any
        elem[this._addEventListener](this._prefix + eventName, this._support === 'wheel' ? callback : (originalEvent) => {
            !originalEvent && (originalEvent = window.event);
            // create a normalized event object
            const event = {
                isWheelPolyfillEvent: true,
                // keep a ref to the original event object
                originalEvent,
                target: originalEvent.target || originalEvent.srcElement,
                type: 'wheel',
                deltaMode: originalEvent.type === 'MozMousePixelScroll' ? 0 : 1,
                deltaX: 0,
                deltaY: 0,
                deltaZ: 0,
                preventDefault: () => {
                    originalEvent.preventDefault ?
                        originalEvent.preventDefault() :
                        originalEvent.returnValue = false;
                },
            };
            // calculate deltaY (and deltaX) according to the event
            if (this._support === 'mousewheel') {
                // tslint:disable-next-line:no-any
                event.deltaY = -1 / 40 * originalEvent.wheelDelta;
                // Webkit also support wheelDeltaX
                // tslint:disable-next-line:no-any
                originalEvent.wheelDeltaX && (event.deltaX = -1 / 40 * originalEvent.wheelDeltaX);
            }
            else {
                event.deltaY = originalEvent.deltaY || originalEvent.detail;
            }
            // it's time to fire the callback
            return callback(event);
        }, useCapture || false);
    }
}
WheelListener.deltaOneWheelFactor = 40;
//# sourceMappingURL=WheelListener.js.map